// Catalog Preview Component

import { PreviewState } from './types/preview';
import { previewAPI, PREVIEW_PAGE_SIZE } from './preview-api';
import { debugConsole } from './debug';

export class CatalogPreview {
  private container: HTMLElement;
  private configHash: string;
  private catalogType: string;
  private catalogId: string;
  private state: PreviewState;
  private abortController?: AbortController;
  private loadingDebounceTimer?: number;
  private scrollElement?: HTMLElement;
  private loadingMore = false;
  private hasBeenLoaded = false;

  constructor(
    container: HTMLElement,
    configHash: string,
    catalogType: string,
    catalogId: string
  ) {
    debugConsole.log(`[CatalogPreview] Constructor called for ${catalogId} (type: ${catalogType})`);
    debugConsole.log(`[CatalogPreview] configHash: ${configHash.substring(0, 20)}...`);

    this.container = container;
    this.configHash = configHash;
    this.catalogType = catalogType;
    this.catalogId = catalogId;
    this.state = {
      loading: false,
      items: [],
      error: null,
      catalogId: catalogId,
      page: 0,
      totalItems: 0,
      itemsPerPage: PREVIEW_PAGE_SIZE,
      hasMore: true
    };
    this.hasBeenLoaded = false;

    debugConsole.log(`[CatalogPreview] Initial state set - loading: false, items: 0, hasMore: true`);

    this.scrollElement = container.querySelector('.preview-scroll') as HTMLElement;
    if (!this.scrollElement) {
      debugConsole.log(`[CatalogPreview] ERROR: Could not find .preview-scroll element for ${catalogId}`);
    } else {
      debugConsole.log(`[CatalogPreview] Found scroll element for ${catalogId}`);
    }

    this.setupScrollListener();
    this.setupRetryButton();

    // Render initial state (shows empty state since items.length === 0)
    debugConsole.log(`[CatalogPreview] Calling initial render() for ${catalogId}`);
    this.render();

    debugConsole.log(`[CatalogPreview] ✅ Initialized for ${catalogId} (type: ${catalogType})`);
  }

  async loadPreview(): Promise<void> {
    debugConsole.log(`[CatalogPreview] loadPreview called for ${this.catalogId}`);

    // Cancel any existing requests
    if (this.abortController) {
      this.abortController.abort();
    }

    // Clear debounce timer
    if (this.loadingDebounceTimer) {
      clearTimeout(this.loadingDebounceTimer);
    }

    // Debounce loading
    this.loadingDebounceTimer = window.setTimeout(async () => {
      try {
        this.abortController = new AbortController();
        // Don't clear items - keep them visible during reload with overlay
        this.setState({ loading: true, error: null });

        const result = await previewAPI.fetchCatalogItems(
          this.configHash,
          this.catalogType,
          this.catalogId,
          0,
          this.abortController.signal
        );

        this.hasBeenLoaded = true;
        this.setState({
          loading: false,
          items: result.items,
          error: null,
          page: 0,
          hasMore: result.hasMore
        });

        debugConsole.log(`[CatalogPreview] Loaded ${result.items.length} items for ${this.catalogId}`);

      } catch (error: any) {
        if (error.name !== 'AbortError') {
          this.hasBeenLoaded = true;
          this.setState({
            loading: false,
            error: this.getErrorMessage(error)
          });
          debugConsole.error(`[CatalogPreview] Load failed: ${error.message}`);
        }
      }
    }, 300);
  }

  async loadNextPage(): Promise<void> {
    if (this.loadingMore || !this.state.hasMore || this.state.loading) {
      return;
    }

    // Don't load more than 50 items total
    if (this.state.items.length >= 50) {
      this.setState({ hasMore: false });
      return;
    }

    this.loadingMore = true;
    debugConsole.log(`[CatalogPreview] Loading next page for ${this.catalogId}, current items: ${this.state.items.length}`);

    try {
      const nextPage = this.state.page + 1;
      const result = await previewAPI.fetchCatalogItems(
        this.configHash,
        this.catalogType,
        this.catalogId,
        nextPage * PREVIEW_PAGE_SIZE
      );

      this.setState({
        items: [...this.state.items, ...result.items],
        hasMore: result.hasMore && (this.state.items.length + result.items.length < 50),
        page: nextPage
      });

      debugConsole.log(`[CatalogPreview] Loaded page ${nextPage}, total items: ${this.state.items.length}`);

    } catch (error: any) {
      debugConsole.error(`[CatalogPreview] Failed to load next page: ${error.message}`);
    } finally {
      this.loadingMore = false;
    }
  }

  clear(): void {
    debugConsole.log(`[CatalogPreview] Clearing preview for ${this.catalogId}`);

    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.loadingDebounceTimer) {
      clearTimeout(this.loadingDebounceTimer);
    }

    this.hasBeenLoaded = false;
    this.setState({
      loading: false,
      items: [],
      error: null,
      page: 0,
      totalItems: 0,
      hasMore: true
    });
  }

  destroy(): void {
    debugConsole.log(`[CatalogPreview] Destroying preview for ${this.catalogId}`);

    this.clear();
    if (this.scrollElement) {
      this.scrollElement.removeEventListener('scroll', this.onScroll);
    }
  }

  private setState(updates: Partial<PreviewState>): void {
    debugConsole.log(`[CatalogPreview] setState called for ${this.catalogId}: ` + JSON.stringify({
      loading: updates.loading,
      itemCount: updates.items?.length,
      hasMore: updates.hasMore,
      page: updates.page,
      error: updates.error
    }));

    this.state = { ...this.state, ...updates };

    debugConsole.log(`[CatalogPreview] New state for ${this.catalogId}: ` + JSON.stringify({
      loading: this.state.loading,
      itemCount: this.state.items.length,
      error: this.state.error,
      hasMore: this.state.hasMore,
      page: this.state.page
    }));

    this.render();
  }

  private setupScrollListener(): void {
    if (this.scrollElement) {
      this.scrollElement.addEventListener('scroll', this.onScroll.bind(this));
      debugConsole.log(`[CatalogPreview] Scroll listener registered for ${this.catalogId}`);
    } else {
      debugConsole.log(`[CatalogPreview] WARNING: No scroll element found, scroll listener not registered for ${this.catalogId}`);
    }
  }

  private setupRetryButton(): void {
    const retryButton = this.container.querySelector('.retry-button');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        debugConsole.log(`[CatalogPreview] Retry button clicked for ${this.catalogId}`);
        this.loadPreview();
      });
      debugConsole.log(`[CatalogPreview] Retry button listener registered for ${this.catalogId}`);
    } else {
      debugConsole.log(`[CatalogPreview] WARNING: No retry button found for ${this.catalogId}`);
    }
  }

  private onScroll(): void {
    if (!this.scrollElement || this.loadingMore) {
      return;
    }

    const scrollLeft = this.scrollElement.scrollLeft;
    const scrollWidth = this.scrollElement.scrollWidth;
    const clientWidth = this.scrollElement.clientWidth;
    const scrollPercentage = ((scrollLeft + clientWidth) / scrollWidth * 100).toFixed(1);

    debugConsole.log(`[CatalogPreview] Scroll event for ${this.catalogId}: ${scrollPercentage}% (left: ${scrollLeft}, width: ${scrollWidth}, client: ${clientWidth})`);

    // Load more when scrolled to 70% of content
    if (scrollLeft + clientWidth >= scrollWidth * 0.7) {
      debugConsole.log(`[CatalogPreview] Scroll threshold reached (70%), triggering loadNextPage for ${this.catalogId}`);
      this.loadNextPage();
    }
  }

  private getErrorMessage(error: any): string {
    if (error.message.includes('404')) {
      return 'Catalog not found';
    } else if (error.message.includes('401')) {
      return 'Invalid auth key';
    } else if (error.message.includes('429')) {
      return 'Too many requests. Please wait.';
    } else {
      return 'Failed to load catalog items';
    }
  }

  private render(): void {
    debugConsole.log(`[CatalogPreview] render() called for ${this.catalogId}`);

    const loading = this.container.querySelector('.preview-loading') as HTMLElement;
    const overlay = this.container.querySelector('.preview-loading-overlay') as HTMLElement;
    const empty = this.container.querySelector('.preview-empty') as HTMLElement;
    const error = this.container.querySelector('.preview-error') as HTMLElement;
    const content = this.container.querySelector('.preview-content') as HTMLElement;

    debugConsole.log(`[CatalogPreview] Found DOM elements - loading: ${!!loading}, overlay: ${!!overlay}, empty: ${!!empty}, error: ${!!error}, content: ${!!content}`);

    // Hide all states
    if (loading) loading.style.display = 'none';
    if (overlay) overlay.style.display = 'none';
    if (empty) empty.style.display = 'none';
    if (error) error.style.display = 'none';
    if (content) content.style.display = 'none';

    // Show appropriate state
    if (this.state.loading && this.state.items.length === 0) {
      // Initial load: show loading skeleton
      debugConsole.log(`[CatalogPreview] Showing LOADING SKELETON state for ${this.catalogId}`);
      if (loading) loading.style.display = 'flex';
    } else if (this.state.loading && this.state.items.length > 0) {
      // Reload: show content with overlay
      debugConsole.log(`[CatalogPreview] Showing CONTENT WITH OVERLAY state for ${this.catalogId}`);
      if (content) content.style.display = 'block';
      if (overlay) overlay.style.display = 'flex';
    } else if (this.state.error) {
      debugConsole.log(`[CatalogPreview] Showing ERROR state for ${this.catalogId}: ${this.state.error}`);
      if (error) {
        error.style.display = 'flex';
        const errorMessage = error.querySelector('.error-message');
        if (errorMessage) {
          errorMessage.textContent = this.state.error || 'Failed to load items';
        }
      }
    } else if (this.state.items.length === 0) {
      debugConsole.log(`[CatalogPreview] Showing EMPTY state for ${this.catalogId}`);
      if (empty) {
        empty.style.display = 'flex';
        // Update message based on whether catalog has been loaded
        const emptyMessage = empty.querySelector('span');
        if (emptyMessage) {
          if (this.hasBeenLoaded) {
            emptyMessage.textContent = 'No items found in your library';
          } else {
            emptyMessage.textContent = 'Enable this catalog to preview';
          }
        }
      }
    } else {
      debugConsole.log(`[CatalogPreview] Showing CONTENT state for ${this.catalogId} with ${this.state.items.length} items`);
      if (content) {
        content.style.display = 'block';
        this.renderItems();
      }
    }

    debugConsole.log(`[CatalogPreview] ✅ render() completed for ${this.catalogId}`);
  }

  private renderItems(): void {
    debugConsole.log(`[CatalogPreview] renderItems() called for ${this.catalogId} with ${this.state.items.length} items`);

    if (!this.scrollElement) {
      debugConsole.log(`[CatalogPreview] ERROR: scrollElement is null, cannot render items`);
      return;
    }

    this.scrollElement.innerHTML = '';

    let renderedCount = 0;
    this.state.items.forEach(item => {
      if (!this.scrollElement) return; // Type guard for strictNullChecks

      const itemElement = document.createElement('div');
      itemElement.className = 'preview-item';

      const poster = document.createElement('div');
      poster.className = 'preview-poster';

      if (item.poster) {
        const img = document.createElement('img');
        img.src = item.poster;
        img.alt = item.name;
        img.loading = 'lazy';
        poster.appendChild(img);
      } else {
        // Default poster placeholder
        poster.style.background = 'var(--bg-secondary)';
        poster.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--text-muted);font-size:24px;">📺</div>';
      }

      const title = document.createElement('div');
      title.className = 'preview-title';
      title.textContent = item.name;
      if (item.year) {
        title.textContent += ` (${item.year})`;
      }

      itemElement.appendChild(poster);
      itemElement.appendChild(title);
      this.scrollElement.appendChild(itemElement);
      renderedCount++;
    });

    debugConsole.log(`[CatalogPreview] ✅ Rendered ${renderedCount} items for ${this.catalogId}`);
  }
}
